import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '../core/Button';
import { useState } from 'react';
import { useAdminTrackingsQuery } from '../../graphql/hooks/useAdminDashboard';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, format } from 'date-fns'

interface CalendarEntry {
  id: string;
  employeeName: string;
  type: string;
  date: string;
}

interface RequestsCalendarViewProps {
  calendarDate: Date;
  setCalendarDate: (date: Date) => void;
  calendarView: 'Month' | 'Week' | 'Day';
  setCalendarView: (view: 'Month' | 'Week' | 'Day') => void;
}

export function RequestsCalendarView({
  calendarDate,
  setCalendarDate,
  calendarView,
  setCalendarView,
}: RequestsCalendarViewProps) {
  const [popoverDate, setPopoverDate] = useState<string | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);

  const timeSlots = [
    '6am', '7am', '8am', '9am', '10am', '11am', '12pm',
    '1pm', '2pm', '3pm', '4pm', '5pm', '6pm', '7pm'
  ];

  const toLocalDateStr = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const parseLocalDateStr = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const getDateRange = () => {
    if (calendarView === 'Month') {
      return {
        startDate: format(startOfMonth(calendarDate), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(calendarDate), 'yyyy-MM-dd'),
      }
    }
    if (calendarView === 'Week') {
      return {
        startDate: format(startOfWeek(calendarDate, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        endDate: format(endOfWeek(calendarDate, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      }
    }
    return {
      startDate: format(calendarDate, 'yyyy-MM-dd'),
      endDate: format(calendarDate, 'yyyy-MM-dd'),
    }
  }

  const { startDate, endDate } = getDateRange()

  const { data: trackings = [], isLoading } = useAdminTrackingsQuery({ startDate, endDate })

  const mapType = (type: string) => {
    switch (type) {
      case 'holiday': return 'Urlaub';
      case 'overtime': return 'Überstunden';
      case 'sick': return 'Krank';
      default: return type;
    }
  };

  const calendarEntries: CalendarEntry[] = trackings
    .filter(tracking => tracking.type === 'holiday' || tracking.type === 'overtime')
    .map((tracking) => ({
      id: tracking.id,
      employeeName: tracking.employeeName,
      type: mapType(tracking.type),
      date: tracking.date.slice(0, 10),
    }))

  const getWeekStart = (date: Date) => {
    const d = new Date(date);
    const mondayOffset = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - mondayOffset);
    return d;
  };

  const getWeekDays = (startDate: Date) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const navigatePrevious = () => {
    const newDate = new Date(calendarDate);
    if (calendarView === 'Day') newDate.setDate(newDate.getDate() - 1);
    else if (calendarView === 'Week') newDate.setDate(newDate.getDate() - 7);
    else newDate.setMonth(newDate.getMonth() - 1);
    setCalendarDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(calendarDate);
    if (calendarView === 'Day') newDate.setDate(newDate.getDate() + 1);
    else if (calendarView === 'Week') newDate.setDate(newDate.getDate() + 7);
    else newDate.setMonth(newDate.getMonth() + 1);
    setCalendarDate(newDate);
  };

  const goToToday = () => setCalendarDate(new Date());

  const getEntriesForDate = (date: Date) => {
    const dateStr = toLocalDateStr(date);
    return calendarEntries.filter(entry => entry.date === dateStr);
  };

  const getHeaderText = () => {
    if (calendarView === 'Day') {
      return calendarDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).toUpperCase();
    } else if (calendarView === 'Week') {
      const weekStart = getWeekStart(calendarDate);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      const startMonth = weekStart.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
      const endMonth = weekEnd.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
      const startDay = weekStart.getDate();
      const endDay = weekEnd.getDate();
      const year = weekStart.getFullYear();
      if (startMonth === endMonth) {
        return `${startMonth} ${startDay} – ${endDay}, ${year}`;
      } else {
        return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${year}`;
      }
    } else {
      return calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();
    }
  };

  const renderWeekView = () => {
    const weekStart = getWeekStart(calendarDate);
    const weekDays = getWeekDays(weekStart);

    return (
      <div className="border border-[#e9e9e9] rounded-[8px] overflow-hidden bg-white">
        <div className="grid grid-cols-[80px_repeat(7,1fr)] bg-[#f5f5f5] border-b border-[#e9e9e9]">
          <div></div>
          {weekDays.map((day, index) => {
            const dayNames = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
            return (
              <div key={index} className="px-3 py-3 text-center border-l border-[#e9e9e9] first:border-l-0">
                <div className="text-[11px] font-semibold text-[#6b6b6b] tracking-wide">
                  {dayNames[index]} {day.getMonth() + 1}/{day.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-[80px_repeat(7,1fr)] border-b border-[#e9e9e9] min-h-[80px]">
          <div className="px-3 py-2 text-[13px] text-[#6b6b6b] border-r border-[#e9e9e9]">all-day</div>
          {weekDays.map((day, index) => {
            const entries = getEntriesForDate(day);
            const visibleEntries = entries.slice(0, 2);
            const moreCount = entries.length - visibleEntries.length;
            return (
              <div key={index} className="px-2 py-2 border-l border-[#e9e9e9] first:border-l-0 space-y-1">
                {visibleEntries.map(entry => (
                  <div
                    key={entry.id}
                    className={`text-[11px] px-2 py-1 rounded-[4px] truncate font-medium ${
                      entry.type === 'Urlaub' ? 'bg-[#e0f2f1] text-[#00897b]' : 'bg-[#fff8e1] text-[#f57c00]'
                    }`}
                  >
                    {entry.type}: {entry.employeeName}
                  </div>
                ))}
                {moreCount > 0 && (
                  <button
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setPopoverDate(toLocalDateStr(day));
                      setPopoverPosition({ top: rect.bottom + 4, left: rect.left });
                    }}
                    className="text-[11px] text-[#6b6b6b] pl-2 hover:text-[#1c1c1c] cursor-pointer"
                  >
                    +{moreCount} more
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="overflow-y-auto max-h-[600px]">
          {timeSlots.map((time) => (
            <div key={time} className="grid grid-cols-[80px_repeat(7,1fr)] border-b border-[#e9e9e9] last:border-b-0 min-h-[60px]">
              <div className="px-3 py-2 text-[13px] text-[#6b6b6b] border-r border-[#e9e9e9]">{time}</div>
              {weekDays.map((_, dayIndex) => (
                <div key={dayIndex} className="border-l border-[#e9e9e9] first:border-l-0 hover:bg-[#f5f5f5] transition-colors" />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const dayName = calendarDate.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
    const entries = getEntriesForDate(calendarDate);
    const visibleEntries = entries.slice(0, 4);
    const moreCount = entries.length - visibleEntries.length;

    return (
      <div className="border border-[#e9e9e9] rounded-[8px] overflow-hidden bg-white">
        <div className="grid grid-cols-[auto_1fr] bg-[#f5f5f5] border-b border-[#e9e9e9]">
          <div className="w-20"></div>
          <div className="px-3 py-3 text-center border-l border-[#e9e9e9]">
            <div className="text-[11px] font-semibold text-[#6b6b6b] tracking-wide">{dayName}</div>
          </div>
        </div>

        <div className="grid grid-cols-[auto_1fr] border-b border-[#e9e9e9] min-h-[120px]">
          <div className="w-20 px-3 py-2 text-[13px] text-[#6b6b6b] border-r border-[#e9e9e9]">all-day</div>
          <div className="px-4 py-3 border-l border-[#e9e9e9] space-y-2">
            {visibleEntries.map(entry => (
              <div
                key={entry.id}
                className={`text-[13px] px-3 py-2 rounded-[4px] font-medium ${
                  entry.type === 'Urlaub' ? 'bg-[#e0f2f1] text-[#00897b]' : 'bg-[#fff8e1] text-[#f57c00]'
                }`}
              >
                {entry.type}: {entry.employeeName}
              </div>
            ))}
            {moreCount > 0 && (
              <div className="text-[13px] text-[#6b6b6b] px-3">+{moreCount} more</div>
            )}
          </div>
        </div>

        <div className="overflow-y-auto max-h-[600px]">
          {timeSlots.map((time) => (
            <div key={time} className="grid grid-cols-[auto_1fr] border-b border-[#e9e9e9] last:border-b-0 min-h-[60px]">
              <div className="w-20 px-3 py-2 text-[13px] text-[#6b6b6b] border-r border-[#e9e9e9]">{time}</div>
              <div className="border-l border-[#e9e9e9] hover:bg-[#f5f5f5] transition-colors"></div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDay = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const calendarDays = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      calendarDays.push({ day, date: new Date(year, month - 1, day), isCurrentMonth: false });
    }
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push({ day, date: new Date(year, month, day), isCurrentMonth: true });
    }
    const remainingDays = 42 - calendarDays.length;
    for (let day = 1; day <= remainingDays; day++) {
      calendarDays.push({ day, date: new Date(year, month + 1, day), isCurrentMonth: false });
    }

    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }

    return (
      <div className="border border-[#e9e9e9] rounded-[8px] overflow-hidden bg-white">
        <div className="grid grid-cols-7 bg-[#f5f5f5] border-b border-[#e9e9e9]">
          {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((day) => (
            <div key={day} className="px-4 py-3 text-[11px] font-semibold text-[#6b6b6b] text-center border-r border-[#e9e9e9] last:border-r-0 tracking-wide">
              {day}
            </div>
          ))}
        </div>

        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 border-t border-[#e9e9e9] first:border-t-0">
            {week.map((dayInfo, dayIndex) => {
              const dateStr = toLocalDateStr(dayInfo.date);
              const dayEntries = calendarEntries.filter(entry => entry.date === dateStr);
              const visibleEntries = dayEntries.slice(0, 1);
              const moreCount = dayEntries.length - visibleEntries.length;

              return (
                <div
                  key={dayIndex}
                  className={`min-h-[120px] p-3 border-r border-[#e9e9e9] last:border-r-0 relative ${
                    dayInfo.isCurrentMonth
                      ? 'bg-white'
                      : 'bg-[repeating-linear-gradient(45deg,#fafafa,#fafafa_10px,#f5f5f5_10px,#f5f5f5_20px)]'
                  }`}
                >
                  <div className={`text-[14px] mb-2 ${dayInfo.isCurrentMonth ? 'text-[#1c1c1c]' : 'text-[#9e9e9e]'}`}>
                    {dayInfo.day}
                  </div>
                  <div className="space-y-1">
                    {visibleEntries.map((entry) => (
                      <div
                        key={entry.id}
                        className={`text-[11px] px-2 py-1 rounded-[4px] truncate font-medium ${
                          entry.type === 'Urlaub' ? 'bg-[#e0f2f1] text-[#00897b]' : 'bg-[#fff8e1] text-[#f57c00]'
                        }`}
                      >
                        {entry.type}: {entry.employeeName}
                      </div>
                    ))}
                    {moreCount > 0 && (
                      <button
                        onClick={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setPopoverDate(dateStr);
                          setPopoverPosition({ top: rect.bottom + 4, left: rect.left });
                        }}
                        className="text-[11px] text-[#6b6b6b] pl-2 hover:text-[#1c1c1c] cursor-pointer"
                      >
                        +{moreCount} more
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6 relative">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={navigatePrevious} className="p-2 hover:bg-[#f5f5f5] rounded-[6px] transition-colors">
            <ChevronLeft className="size-5 text-[#6b6b6b]" />
          </button>
          <button onClick={navigateNext} className="p-2 hover:bg-[#f5f5f5] rounded-[6px] transition-colors">
            <ChevronRight className="size-5 text-[#6b6b6b]" />
          </button>
          <Button variant="secondary" size="sm" onClick={goToToday} className="bg-white border-[#e9e9e9] text-[#0084c7] hover:bg-[#e3f2fd]">
            Today
          </Button>
        </div>

        <h2 className="text-[#1c1c1c] text-[16px] font-semibold tracking-wide">{getHeaderText()}</h2>

        <div className="flex items-center gap-1">
          {['Month', 'Week', 'Day'].map((view) => (
            <button
              key={view}
              onClick={() => setCalendarView(view as 'Month' | 'Week' | 'Day')}
              className={`px-4 py-2 text-[14px] rounded-[6px] transition-colors ${
                calendarView === view ? 'text-[#0084c7] font-medium' : 'text-[#6b6b6b] hover:text-[#1c1c1c]'
              }`}
            >
              {view}
            </button>
          ))}
        </div>
      </div>

      {calendarView === 'Week' && renderWeekView()}
      {calendarView === 'Day' && renderDayView()}
      {calendarView === 'Month' && renderMonthView()}

      {popoverDate && popoverPosition && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setPopoverDate(null); setPopoverPosition(null); }} />
          <div
            className="fixed z-50 bg-white rounded-[8px] shadow-[0_4px_20px_rgba(0,0,0,0.15)] border border-[#e9e9e9] p-4 min-w-[280px]"
            style={{ top: `${popoverPosition.top}px`, left: `${popoverPosition.left}px` }}
          >
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-[#e9e9e9]">
              <div className="text-[13px] text-[#1c1c1c] font-medium">
                {parseLocalDateStr(popoverDate).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
              <button onClick={() => { setPopoverDate(null); setPopoverPosition(null); }} className="p-1 hover:bg-[#f5f5f5] rounded-[4px] transition-colors">
                <X className="size-4 text-[#6b6b6b]" />
              </button>
            </div>

            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {calendarEntries
                .filter(entry => entry.date === popoverDate)
                .map((entry) => (
                  <div
                    key={entry.id}
                    className={`text-[13px] px-3 py-2 rounded-[4px] font-medium ${
                      entry.type === 'Urlaub' ? 'bg-[#e0f2f1] text-[#00897b]' : 'bg-[#fff8e1] text-[#f57c00]'
                    }`}
                  >
                    {entry.type}: {entry.employeeName}
                  </div>
                ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}